/**
 * Saldos.tsx — el saldo de caja y bancos a una fecha de corte.
 *
 * Nace de que el saldo del banco sólo se veía dentro de la conciliación, por
 * cuenta y por mes. Acá se ven TODAS las cuentas de efectivo de un vistazo, cada
 * una en su moneda, a la fecha que se elija —hoy, un fin de mes, un corte a
 * mitad de mes—. El dato sale de `control.saldos_de_efectivo` (0135), la misma
 * fórmula que la conciliación, así que no discrepan.
 *
 * Es un componente reutilizable: el Tablero y Banco lo muestran a hoy, y
 * Reportes lo deja consultar a cualquier fecha y exportarlo.
 */
import { useCallback, useEffect, useState } from 'react';
import { useIdioma } from '../nucleo/idioma.tsx';
import { useSesion } from '../nucleo/sesion.tsx';
import { hoyISO } from '../nucleo/hoy.ts';
import { descargarArchivo } from '../nucleo/descargar.ts';
import { FalloDeApi, traerSaldosDeEfectivo, type SaldoDeEfectivo } from '../datos/libros.ts';

export function Saldos(
  { conFecha = false, conExportar = false }: { conFecha?: boolean; conExportar?: boolean },
) {
  const { t, monto, fecha: comoFecha } = useIdioma();
  const { pedir } = useSesion();
  const [fecha, setFecha] = useState(hoyISO());
  const [filas, setFilas] = useState<readonly SaldoDeEfectivo[] | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    setFallo(null);
    try {
      const r = await traerSaldosDeEfectivo(pedir, fecha);
      setFilas(r.saldos);
    } catch (e) {
      setFilas(null);
      setFallo(e instanceof FalloDeApi && e.clase === 'conexion'
        ? 'sb.conexion' : 'sb.unknown');
    }
  }, [pedir, fecha]);

  useEffect(() => { void cargar(); }, [cargar]);

  function exportar() {
    if (!filas) return;
    // CSV sencillo del lado del cliente: los saldos ya son texto, no hay
    // aritmética. La cabecera es un identificador y no se traduce.
    const bom = '﻿';
    const lineas = [
      ['codigo', 'cuenta', 'moneda', 'saldo', 'a_la_fecha'].join(','),
      ...filas.map((f) => [
        f.codigo,
        `"${f.nombre.replace(/"/g, '""')}"`,
        f.moneda, f.saldo, fecha,
      ].join(',')),
    ];
    descargarArchivo(`saldos_de_efectivo_${fecha}.csv`,
      new Blob([bom + lineas.join('\r\n')], { type: 'text/csv;charset=utf-8' }));
  }

  return (
    <section className="card">
      <div className="card__head">
        <h3>{t('bal.title')}</h3>
        <span className="spacer" />
        {conFecha && (
          <label className="field field--inline" style={{ marginBottom: 0 }}>
            <span className="field__label">{t('bal.asOf')}</span>
            <input
              className="input" type="date" value={fecha}
              onChange={(e) => setFecha(e.target.value)}
            />
          </label>
        )}
        {conExportar && filas !== null && filas.length > 0 && (
          <button className="btn btn--ghost btn--sm" type="button" onClick={exportar}>
            {t('bal.export')}
          </button>
        )}
      </div>

      {fallo !== null && <p className="muted" style={{ padding: 'var(--s5)' }}>{t(fallo as never)}</p>}

      {fallo === null && (
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>{t('bal.account')}</th>
                <th>{t('doc.currency')}</th>
                <th style={{ textAlign: 'right' }}>{t('bal.balance')}</th>
              </tr>
            </thead>
            <tbody>
              {(filas ?? []).map((f) => (
                <tr key={f.codigo}>
                  <td className="small"><span className="mono">{f.codigo}</span> {f.nombre}</td>
                  <td className="mono small">{f.moneda}</td>
                  <td className="amount">{monto({ valor: f.saldo, moneda: f.moneda })}</td>
                </tr>
              ))}
              {filas !== null && filas.length === 0 && (
                <tr>
                  <td colSpan={3} className="muted" style={{ textAlign: 'center' }}>
                    {t('bal.none')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {conFecha && filas !== null && (
        <p className="small muted" style={{ padding: '0 var(--s5) var(--s5)' }}>
          {t('bal.asOfNote', { fecha: comoFecha(fecha) })}
        </p>
      )}
    </section>
  );
}
